import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

const DAY_NAMES = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];
const MONTH_NAMES = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

const pad = (n) => String(n).padStart(2, '0');

/**
 * CalEventTimePicker — spinner-style time + date picker.
 * Ported from intake_v2_time_widget_template (vanilla JS) to React/MUI.
 * MVP — spinner buttons + day navigation. Keyboard digit-buffer entry follows later.
 * @param {object} props
 * @param {{ top: number, left: number }} props.anchorPos
 * @param {string} props.dateValue - YYYY-MM-DD, or ''
 * @param {string} props.timeValue - HH:MM, or ''
 * @param {function} props.onSave - fn(isoDate, hhmm)
 * @param {function} props.onClose
 */
export default function CalEventTimePicker({ anchorPos, dateValue, timeValue, onSave, onClose }) {
    const popupRef = useRef(null);

    const initialDate = dateValue ? new Date(dateValue) : new Date();
    const [date, setDate] = useState(new Date(initialDate.getFullYear(), initialDate.getMonth(), initialDate.getDate()));

    const [hh, tt] = (timeValue || '00:00').split(':').map(Number);
    const [h, setH] = useState(hh || 0);
    const [m, setM] = useState(tt || 0);

    // Which spinner has keyboard focus — 'h' or 'm'
    const [focus, setFocus] = useState('h');
    // Digit buffer for two-key entry (e.g. '1' then '4' -> 14)
    const bufferRef = useRef('');

    // Refs for the current h/m values, kept in sync via effects below —
    // needed so the keydown handler (registered once) always sees fresh values
    const stateRef = useRef({ date, h, m, focus });
    useEffect(() => {
        stateRef.current = { date, h, m, focus };
    }, [date, h, m, focus]);

    // Close on outside click
    useEffect(() => {
        const handleClick = (e) => {
            if (popupRef.current && !popupRef.current.contains(e.target)) onClose();
        };
        const t = setTimeout(() => document.addEventListener('mousedown', handleClick), 50);
        return () => {
            clearTimeout(t);
            document.removeEventListener('mousedown', handleClick);
        };
    }, [onClose]);

    const shiftDay = (delta) => {
        setDate((d) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + delta));
    };

    const incH = (delta) => setH((v) => (v + delta + 24) % 24);
    const incM = (delta) => setM((v) => (v + delta + 60) % 60);

    const toISODate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    /**
     * Saves current date/time and closes — reads from stateRef for freshness.
     */
    const handleSave = () => {
        const s = stateRef.current;
        onSave(toISODate(s.date), `${pad(s.h)}:${pad(s.m)}`);
        onClose();
    };

    /**
     * Switches keyboard focus between hour and minute spinner, resets digit buffer.
     * @param {'h'|'m'} field
     */
    const switchFocus = (field) => {
        bufferRef.current = '';
        setFocus(field);
    };

    /**
     * Handles digit entry for the focused spinner.
     * First digit replaces the value directly. Second digit combines with the
     * first — if the combined value is valid it's applied and focus advances
     * (hour -> minute, minute -> save). If not valid, the second digit starts
     * a new single-digit entry.
     * @param {'h'|'m'} field
     * @param {number} digit
     * @param {number} max - exclusive upper bound (24 for hours, 60 for minutes)
     * @param {function} setValue
     */
    const handleDigit = (field, digit, max, setValue) => {
        if (bufferRef.current.length === 0) {
            bufferRef.current = String(digit);
            setValue(digit);
            return;
        }

        const combined = Number(bufferRef.current + String(digit));
        if (combined < max) {
            setValue(combined);
            bufferRef.current = '';
            if (field === 'h') {
                switchFocus('m');
            } else {
                handleSave();
            }
        } else {
            bufferRef.current = String(digit);
            setValue(digit);
        }
    };

    // Keyboard handler — registered once, reads live state via stateRef.
    // stopImmediatePropagation on every handled key — this is a global
    // document listener and other components (e.g. YourNotesEntryPanel) have
    // their own global arrow/WASD listeners for entry navigation. Without
    // this, preventDefault alone does not stop those other listeners from
    // also firing on the same keydown — see CalendarEngine.Sessions,
    // "WASD/arrows leak naar YourNotes EntryPanel" 2026-06-15.
    useEffect(() => {
        const handleKey = (e) => {
            const k = e.key;

            if (k === 'Escape') { e.preventDefault(); e.stopImmediatePropagation(); onClose(); return; }
            if (k === 'Enter') { e.preventDefault(); e.stopImmediatePropagation(); handleSave(); return; }
            if (k === 'Tab') {
                e.preventDefault();
                e.stopImmediatePropagation();
                switchFocus(stateRef.current.focus === 'h' ? 'm' : 'h');
                return;
            }
            if (k === 'ArrowUp') {
                e.preventDefault();
                e.stopImmediatePropagation();
                bufferRef.current = '';
                stateRef.current.focus === 'h' ? incH(1) : incM(1);
                return;
            }
            if (k === 'ArrowDown') {
                e.preventDefault();
                e.stopImmediatePropagation();
                bufferRef.current = '';
                stateRef.current.focus === 'h' ? incH(-1) : incM(-1);
                return;
            }
            if (k === 'ArrowLeft') {
                e.preventDefault();
                e.stopImmediatePropagation();
                shiftDay(-1);
                return;
            }
            if (k === 'ArrowRight') {
                e.preventDefault();
                e.stopImmediatePropagation();
                shiftDay(1);
                return;
            }
            // WASD — W/S increment/decrement active spinner, A/D shift day
            if (k === 'w' || k === 'W') {
                e.preventDefault();
                e.stopImmediatePropagation();
                bufferRef.current = '';
                stateRef.current.focus === 'h' ? incH(1) : incM(1);
                return;
            }
            if (k === 's' || k === 'S') {
                e.preventDefault();
                e.stopImmediatePropagation();
                bufferRef.current = '';
                stateRef.current.focus === 'h' ? incH(-1) : incM(-1);
                return;
            }
            if (k === 'a' || k === 'A') {
                e.preventDefault();
                e.stopImmediatePropagation();
                shiftDay(-1);
                return;
            }
            if (k === 'd' || k === 'D') {
                e.preventDefault();
                e.stopImmediatePropagation();
                shiftDay(1);
                return;
            }
            if (k >= '0' && k <= '9') {
                e.preventDefault();
                e.stopImmediatePropagation();
                const digit = Number(k);
                if (stateRef.current.focus === 'h') {
                    handleDigit('h', digit, 24, setH);
                } else {
                    handleDigit('m', digit, 60, setM);
                }
            }
        };

        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, []);

    const popup = (
        <Box ref={popupRef} sx={dialogSx} style={{ top: anchorPos.top, left: anchorPos.left }}>

            {/* Titlebar */}
            <Box sx={titlebarSx}>
                <Typography sx={titlebarLabelSx}>Tijd aanpassen</Typography>
                <IconButton size="small" onClick={onClose} sx={{ p: '1px', color: '#a3d1ff', '&:hover': { color: '#ff6b6b' } }}>
                    <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
            </Box>

            <Box sx={bodySx}>
                {/* Date navigation */}
                <Box sx={dateRowSx}>
                    <IconButton size="small" onClick={() => shiftDay(-1)} sx={dateBtnSx}>
                        <ChevronLeftIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                    <Typography sx={dateLabelSx}>
                        {DAY_NAMES[date.getDay()]} {date.getDate()} {MONTH_NAMES[date.getMonth()]} {date.getFullYear()}
                    </Typography>
                    <IconButton size="small" onClick={() => shiftDay(1)} sx={dateBtnSx}>
                        <ChevronRightIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                </Box>

                {/* Hour / minute spinners */}
                <Box sx={spinnersSx}>
                    <SpinnerColumn
                        value={pad(h)}
                        active={focus === 'h'}
                        onUp={() => { bufferRef.current = ''; incH(1); }}
                        onDown={() => { bufferRef.current = ''; incH(-1); }}
                        onFocus={() => switchFocus('h')}
                    />
                    <Typography sx={sepSx}>:</Typography>
                    <SpinnerColumn
                        value={pad(m)}
                        active={focus === 'm'}
                        onUp={() => { bufferRef.current = ''; incM(1); }}
                        onDown={() => { bufferRef.current = ''; incM(-1); }}
                        onFocus={() => switchFocus('m')}
                    />
                </Box>

                {/* Keyboard hint */}
                <Typography sx={hintSx}>↑↓/WS aanpassen · ←→/AD dag · Tab wisselen · Enter opslaan</Typography>
            </Box>

            <Box sx={footerSx}>
                <button className="ce-btn ce-btn-cancel" onClick={onClose}>Annuleren</button>
                <button className="ce-btn ce-btn-save ready" onClick={handleSave}>Opslaan</button>
            </Box>
        </Box>
    );

    return createPortal(popup, document.body);
}

/**
 * SpinnerColumn — up/down buttons with a value display.
 * @param {object} props
 * @param {string} props.value
 * @param {boolean} props.active - true when this spinner has keyboard focus
 * @param {function} props.onUp
 * @param {function} props.onDown
 * @param {function} props.onFocus - called when the display is clicked
 */
function SpinnerColumn({ value, active, onUp, onDown, onFocus }) {
    return (
        <Box sx={colSx}>
            <IconButton size="small" onClick={onUp} sx={spinnerBtnSx}>
                <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
            </IconButton>
            <Box sx={displaySx(active)} onClick={onFocus}>{value}</Box>
            <IconButton size="small" onClick={onDown} sx={spinnerBtnSx}>
                <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
            </IconButton>
        </Box>
    );
}

// ─── sx ──────────────────────────────────────────

const dialogSx = {
    position: 'fixed',
    zIndex: 10001,
    background: '#0d1f33',
    border: '0.5px solid #294766',
    borderRadius: '10px',
    minWidth: 220,
    overflow: 'hidden',
    boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
    fontFamily: "'DM Sans', sans-serif",
};

const titlebarSx = {
    background: '#05407a',
    px: '10px',
    py: '5px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
};

const titlebarLabelSx = {
    fontSize: 12,
    color: '#a3d1ff',
    fontWeight: 600,
};

const bodySx = {
    px: '16px',
    py: '14px',
};

const dateRowSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    mb: '12px',
};

const dateBtnSx = {
    border: '0.5px solid #294766',
    borderRadius: '4px',
    color: '#7fb3d3',
    width: 24,
    height: 24,
    '&:hover': { borderColor: '#66b2ff', color: '#66b2ff' },
};

const dateLabelSx = {
    fontSize: 12,
    color: '#a3d1ff',
    minWidth: 120,
    textAlign: 'center',
    fontWeight: 500,
};

const spinnersSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
};

const colSx = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
};

const spinnerBtnSx = {
    border: '0.5px solid #294766',
    borderRadius: '4px',
    color: '#7fb3d3',
    width: 36,
    height: 24,
    '&:hover': { borderColor: '#4cceac', color: '#4cceac' },
};

const displaySx = (active) => ({
    background: '#0b1e2e',
    border: `0.5px solid ${active ? '#4cceac' : '#294766'}`,
    borderRadius: '6px',
    width: 56,
    height: 44,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 28,
    fontWeight: 500,
    color: '#c8d6e5',
    fontFamily: 'monospace',
    cursor: 'pointer',
});

const hintSx = {
    fontSize: 11,
    color: '#5a7a9a',
    textAlign: 'center',
    mt: '10px',
};

const sepSx = {
    fontSize: 28,
    color: '#4cceac',
    fontWeight: 500,
    mt: '-4px',
    fontFamily: 'monospace',
};

const footerSx = {
    display: 'flex',
    gap: '6px',
    justifyContent: 'flex-end',
    px: '12px',
    py: '6px',
    borderTop: '0.5px solid #294766',
};
